import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { GEN_LABELS } from "../i18n";
import {
  Plus, Loader2, X, Copy, Check, QrCode, BarChart3,
} from "lucide-react";

export default function TrainerDashboard() {
  const { t } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/sessions").then(({ data }) => setSessions(data.sessions));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/sessions", { title });
      setTitle("");
      setShowCreate(false);
      await load();
    } finally {
      setCreating(false);
    }
  };

  const openSession = async (s, tab = "results") => {
    setSelected(s);
    setDetail(null);
    const { data } = await api.get(`/sessions/${s.session_id}/results`);
    setDetail(data);
  };

  const joinUrl = (code) => `${window.location.origin}/join/${code}`;

  const copy = (code) => {
    navigator.clipboard.writeText(joinUrl(code));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="font-heading text-4xl" style={{ color: "var(--text)" }}>{t("dash_title")}</h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>{t("dash_sub")}</p>
          </div>
          <button data-testid="create-session-btn" onClick={() => setShowCreate(true)} className="btn-gold inline-flex items-center gap-2 px-5 py-3">
            <Plus size={18} /> {t("create_session")}
          </button>
        </div>

        {sessions === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin gold-text" size={30} /></div>
        ) : sessions.length === 0 ? (
          <div className="card-amt p-16 text-center" data-testid="no-sessions">
            <QrCode className="mx-auto mb-4 gold-text" size={36} />
            <p style={{ color: "var(--text-dim)" }}>{t("no_sessions")}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sessions.map((s, i) => (
              <div
                key={s.session_id}
                data-testid={`session-card-${i}`}
                className="card-amt p-6 text-left animate-fade-up flex flex-col"
                style={{ opacity: 0, animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-lg tracking-widest gold-text">{s.code}</span>
                  <span className="text-xs px-2 py-1 rounded-sm" style={{ background: "rgba(201,165,87,0.12)", color: "var(--gold)" }}>
                    {s.participant_count} {t("participants")}
                  </span>
                </div>
                <h3 className="font-heading text-xl mb-5" style={{ color: "var(--text)" }}>{s.title}</h3>
                <div className="mt-auto grid grid-cols-2 gap-3">
                  <button
                    data-testid={`view-results-btn-${i}`}
                    onClick={() => openSession(s)}
                    className="btn-gold inline-flex items-center justify-center gap-2 py-2.5 text-sm"
                  >
                    <BarChart3 size={15} /> {t("view_results")}
                  </button>
                  <button
                    data-testid={`show-qr-btn-${i}`}
                    onClick={() => openSession(s)}
                    className="btn-outline-gold inline-flex items-center justify-center gap-2 py-2.5 text-sm"
                  >
                    <QrCode size={15} /> {t("show_qr")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setShowCreate(false)}>
          <div className="card-amt p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="create-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl" style={{ color: "var(--text)" }}>{t("create_session")}</h2>
              <button onClick={() => setShowCreate(false)}><X style={{ color: "var(--text-dim)" }} /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("session_title")}</label>
                <input data-testid="session-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} className="input-amt w-full px-4 py-3 mt-1" placeholder="Q3 Leadership Workshop" />
              </div>
              <button data-testid="session-submit-btn" disabled={creating} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
                {creating && <Loader2 className="animate-spin" size={18} />} {t("create_session")}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* session detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setSelected(null)}>
          <div className="w-full max-w-xl h-full overflow-y-auto p-8" style={{ background: "var(--navy)", borderLeft: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="session-detail">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl" style={{ color: "var(--text)" }}>{selected.title}</h2>
                <span className="font-mono tracking-widest gold-text">{selected.code}</span>
              </div>
              <button onClick={() => setSelected(null)}><X style={{ color: "var(--text-dim)" }} /></button>
            </div>

            {/* QR + join */}
            <div className="card-amt p-6 mb-6 flex items-center gap-5">
              <div className="bg-white p-2 rounded-sm" data-testid="session-qr">
                <QRCodeCanvas value={joinUrl(selected.code)} size={96} fgColor="#0E1F33" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--gold)" }}>{t("scan_qr")}</p>
                <p className="text-sm break-all mb-3" style={{ color: "var(--text-dim)" }}>{joinUrl(selected.code)}</p>
                <button data-testid="copy-link-btn" onClick={() => copy(selected.code)} className="btn-outline-gold inline-flex items-center gap-2 px-3 py-1.5 text-sm">
                  {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy link"}
                </button>
              </div>
            </div>

            {!detail ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin gold-text" size={28} /></div>
            ) : (
              <>
                {/* distribution */}
                {Object.keys(detail.distribution || {}).length > 0 && (
                  <div className="card-amt p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 size={18} className="gold-text" />
                      <h3 className="font-heading text-lg" style={{ color: "var(--text)" }}>{t("distribution")}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(detail.distribution).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                        <span key={type} className="px-3 py-1.5 rounded-sm text-sm font-mono" style={{ background: "rgba(201,165,87,0.12)", color: "var(--gold)" }}>
                          {type} · {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* results */}
                {detail.results.length === 0 ? (
                  <p className="text-sm text-center py-8" style={{ color: "var(--text-dim)" }} data-testid="no-participants">{t("no_participants")}</p>
                ) : (
                  <div className="space-y-3">
                    {detail.results.map((r) => (
                      <a
                        key={r.result_id}
                        href={`/result/${r.result_id}`}
                        className="card-amt p-4 flex items-center justify-between"
                        data-testid={`participant-${r.result_id}`}
                      >
                        <div>
                          <div className="font-medium" style={{ color: "var(--text)" }}>{r.name}</div>
                          <div className="text-xs" style={{ color: "var(--text-dim)" }}>{GEN_LABELS[r.generation]}</div>
                        </div>
                        <span className="font-mono text-lg gold-text">{r.mbti_type}</span>
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
